const PUSH_PUBLIC_KEY_URL = "/api/push/public-key";
const PUSH_SUBSCRIBE_URL = "/api/push/subscribe";

export function isPushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function initPushServiceWorker() {
  if (!isPushSupported()) return null;

  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch (error) {
    console.warn(error);
    return null;
  }
}

async function readJsonResponse(response) {
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error("L'API de notificacions no respon. Reinicia el servidor de desenvolupament.");
  }
  return response.json();
}

async function getPushPublicKey() {
  const response = await fetch(PUSH_PUBLIC_KEY_URL, { headers: { accept: "application/json" } });
  if (response.status === 503) {
    throw new Error("Les notificacions no estan configurades al servidor (claus VAPID).");
  }
  if (!response.ok) {
    await readJsonResponse(response).catch(() => {
      throw new Error("L'API de notificacions no està disponible en aquest entorn.");
    });
    throw new Error("No s'ha pogut obtenir la clau publica de notificacions.");
  }
  const payload = await response.json();
  if (!payload.publicKey) throw new Error("La clau publica de notificacions no es valida.");
  return payload.publicKey;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export async function subscribeToPush(partnerIndex = null, options = {}) {
  if (!options.quiet) {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") throw new Error("Permis denegat");
  } else if (Notification.permission !== "granted") {
    return null;
  }

  await initPushServiceWorker();
  const registration = await navigator.serviceWorker.ready;
  const publicKey = await getPushPublicKey();

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    if (options.quiet) return null;
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (error) {
      console.error(error);
      throw new Error("El navegador no ha pogut crear la subscripció push.");
    }
  }

  const response = await fetch(PUSH_SUBSCRIBE_URL, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      partnerIndex: partnerIndex == null || partnerIndex === "" ? null : Number(partnerIndex),
    }),
  });

  if (response.status === 503) {
    throw new Error("Les notificacions no estan configurades al servidor (claus VAPID).");
  }
  if (!response.ok) {
    await readJsonResponse(response).catch(() => {
      throw new Error("No s'ha pogut registrar la subscripció al servidor.");
    });
    throw new Error("No s'ha pogut registrar la subscripció al servidor.");
  }

  return subscription;
}

export async function getPushStatus() {
  if (!isPushSupported()) {
    return { supported: false, enabled: false, permission: "unsupported", configured: false };
  }

  let configured = false;
  let configError = "";
  try {
    const response = await fetch(PUSH_PUBLIC_KEY_URL, { headers: { accept: "application/json" } });
    configured = response.ok;
    if (response.status === 503) configError = "missing-vapid";
  } catch {
    configured = false;
    configError = "unavailable";
  }

  let subscribed = false;
  try {
    const registration = await navigator.serviceWorker.getRegistration("/sw.js");
    if (registration) subscribed = Boolean(await registration.pushManager.getSubscription());
  } catch {
    subscribed = false;
  }

  return {
    supported: true,
    configured,
    configError,
    enabled: Notification.permission === "granted" && subscribed,
    permission: Notification.permission,
    subscribed,
  };
}
